import { useCallback, useMemo, useRef, useState } from 'react';
import H2 from 'UIComponents/elements/headings/H2';
import I18n from 'I18n';
import UIButton from 'UIComponents/button/UIButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIModal from 'UIComponents/dialog/UIModal';
import UISection from 'UIComponents/section/UISection';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { CROPPER_INVALID_SIZE, DEFAULT_MIN_THUMBNAIL_HEIGHT, DEFAULT_MIN_THUMBNAIL_WIDTH, DEFAULT_REDUCED_CROP_SIZE, DEFAULT_REDUCED_PREVIEW_SIZE, DEFAULT_STANDARD_CROP_HEIGHT, DEFAULT_STANDARD_CROP_WIDTH, ModalDropZone, ThumbnailCropper, getCroppedThumbnailFile } from '..';
import { useUploadFile } from '../../fileUpload';
import Raven from 'raven-js';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const useUploadCroppedThumbnails = () => {
  const {
    uploadFile,
    fileUploadError,
    uploading
  } = useUploadFile();
  const uploadCroppedThumbnails = useCallback(thumbnails => {
    return Promise.all(thumbnails.map(async thumbnail => uploadFile({
      variables: {
        formData: await getCroppedThumbnailFile(thumbnail)
      }
    })));
  }, [uploadFile]);
  return {
    uploadCroppedThumbnails,
    uploading,
    fileUploadError
  };
};
const useThumbnailModal = ({
  onFileUploadSuccess,
  onFileUploadError
}) => {
  const [errorMessage, setErrorMessage] = useState();
  const [hasError, setHasError] = useState(false);
  const handleError = useCallback(error => {
    onFileUploadError(error);
    setHasError(true);
  }, [onFileUploadError]);
  const {
    uploadCroppedThumbnails,
    uploading,
    fileUploadError
  } = useUploadCroppedThumbnails();
  const uploadThumbnails = useCallback((standardThumbnail, reducedThumbnail) => {
    uploadCroppedThumbnails([standardThumbnail, reducedThumbnail]).then(result => {
      if (!result[0].data || !result[1].data) {
        handleError(new Error('Data not loaded'));
        return;
      }
      onFileUploadSuccess(result[0].data.fileManagerData.objects[0].url, result[1].data.fileManagerData.objects[0].url);
    }).catch(error => {
      if (error.message === CROPPER_INVALID_SIZE) {
        const {
          minWidth,
          minHeight,
          imageWidth,
          imageHeight
        } = error.cause;
        setErrorMessage(I18n.text('learningCenter.cropper.resizeInvalidDimensions', {
          minHeight,
          minWidth,
          imageHeight,
          imageWidth
        }));
      } else handleError(error);
    });
  }, [handleError, onFileUploadSuccess, uploadCroppedThumbnails]);
  const failed = useMemo(() => !!fileUploadError || !!errorMessage || hasError, [errorMessage, fileUploadError, hasError]);
  return {
    uploadThumbnails,
    errorMessage,
    uploading,
    failed
  };
};
export const ThumbnailModal = ({
  onImageLoad,
  onClose,
  minImageHeight = DEFAULT_MIN_THUMBNAIL_HEIGHT,
  minImageWidth = DEFAULT_MIN_THUMBNAIL_WIDTH,
  tooltipReduced,
  tooltipStandard,
  description,
  descriptionStandard,
  descriptionReduced
}) => {
  const [thumbnailCropperPreview, setThumbnailCropperPreview] = useState(null);
  const [file, setFile] = useState(null);
  const standardCropper = useRef();
  const reducedCropper = useRef();
  const handleCloseModal = useCallback(() => {
    onClose();
  }, [onClose]);
  const {
    uploadThumbnails,
    errorMessage,
    uploading,
    failed
  } = useThumbnailModal({
    minImageHeight,
    minImageWidth,
    onFileUploadSuccess: (reducedPreviewImage, standardPreviewImage) => {
      onImageLoad(reducedPreviewImage, standardPreviewImage);
      handleCloseModal();
    },
    onFileUploadError: error => {
      Raven.captureException(error);
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: I18n.text('learningCenter.cropper.uploadError')
      });
    }
  });
  const handleCropperUpload = fileArrayBuffer => {
    if (!fileArrayBuffer) return;
    setThumbnailCropperPreview(fileArrayBuffer);
  };
  const handleSaveClick = useCallback(() => {
    if (file) {
      const filename = file.name;
      uploadThumbnails({
        cropper: standardCropper,
        fileName: `standard-${filename}`,
        minHeight: minImageHeight,
        minWidth: minImageWidth
      }, {
        cropper: reducedCropper,
        fileName: `reduced-${filename}`
      });
    }
  }, [file, minImageHeight, minImageWidth, uploadThumbnails]);
  const handleFileUpdate = newFile => setFile(newFile);

  // because this modal is rendered inside navigation popups that close on external clicks we need
  // to make sure we don't propagate clicks inside the modal as they will close the parent navigation
  // popup and (as a consequence) the modal itself
  const handleModalClick = e => e.stopPropagation();
  const hasUploadedFile = !!file;
  return (
    /*#__PURE__*/
    // @ts-expect-error No overload matches this call
    _jsxs(UIModal, {
      onClick: handleModalClick,
      width: 616,
      children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
        children: [/*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.cropper.modalTitle"
          })
        }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
          onClick: handleCloseModal
        })]
      }), /*#__PURE__*/_jsxs(UIDialogBody, {
        children: [description && !hasUploadedFile && description, /*#__PURE__*/_jsx(UISection, {
          children: /*#__PURE__*/_jsx(ModalDropZone, {
            file: file,
            handleCropperUpload: handleCropperUpload,
            handleFileUpdate: handleFileUpdate,
            minImageHeight: minImageHeight,
            minImageWidth: minImageWidth
          })
        }), /*#__PURE__*/_jsx(UISection, {
          children: hasUploadedFile && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(UIFormControl, {
              error: !!errorMessage,
              label: descriptionStandard || /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.cropper.standardSize"
              }),
              tooltip: tooltipStandard,
              validationMessage: errorMessage && /*#__PURE__*/_jsx("span", {
                "data-test-id": "thumbnail-modal-upload-error-message",
                children: errorMessage
              }),
              children: /*#__PURE__*/_jsx(ThumbnailCropper, {
                cropHeight: DEFAULT_STANDARD_CROP_HEIGHT,
                cropType: "square",
                cropWidth: DEFAULT_STANDARD_CROP_WIDTH,
                previewHeight: DEFAULT_STANDARD_CROP_WIDTH,
                ref: standardCropper,
                url: thumbnailCropperPreview
              })
            }), /*#__PURE__*/_jsx(UIFormControl, {
              label: descriptionReduced || /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.cropper.reducedSize"
              }),
              tooltip: tooltipReduced,
              children: /*#__PURE__*/_jsx(ThumbnailCropper, {
                cropHeight: DEFAULT_REDUCED_CROP_SIZE,
                cropType: "square",
                cropWidth: DEFAULT_REDUCED_CROP_SIZE,
                lowerHelpText: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "learningCenter.cropper.sizeHelpText",
                  options: {
                    minWidth: DEFAULT_REDUCED_CROP_SIZE,
                    minHeight: DEFAULT_REDUCED_CROP_SIZE
                  }
                }),
                previewHeight: DEFAULT_REDUCED_PREVIEW_SIZE,
                previewWidth: DEFAULT_REDUCED_PREVIEW_SIZE,
                ref: reducedCropper,
                url: thumbnailCropperPreview,
                zoom: true
              })
            })]
          })
        })]
      }), /*#__PURE__*/_jsxs(UIDialogFooter, {
        children: [/*#__PURE__*/_jsx(UILoadingButton, {
          disabled: !hasUploadedFile,
          failed: failed,
          loading: uploading,
          onClick: handleSaveClick,
          use: "primary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.cropper.modalSave"
          })
        }), /*#__PURE__*/_jsx(UIButton, {
          onClick: handleCloseModal,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.cropper.modalCancel"
          })
        })]
      })]
    })
  );
};
export default ThumbnailModal;